/**
 * Per-account "is this message the account's first push since (re)subscribing,
 * or a delta" tracker for a *batched* WS channel — one hook instance now
 * receives interleaved messages for every account in scope on a single
 * stream, so a single `hasDumpRef` boolean would incorrectly mark the whole
 * subscription "dumped" the moment the first account's dump arrived, treating
 * every other account's still-pending initial dump as a delta (and silently
 * dropping whatever the "delta" merge path assumes was already seeded).
 *
 * Reset this whenever the underlying channel is torn down and resubscribed
 * (i.e. whenever the hook's payload/scope changes) — a fresh subscribe means
 * the server re-sends a fresh dump for every account in the new channel,
 * including ones that were already known under the old channel.
 */
export interface AccountDumpTracker {
  isInitialDump: (accountId: string) => boolean
  markDumped: (accountId: string) => void
  hasDumped: (accountId: string) => boolean
  /**
   * True once *every* account in scope has had its initial dump. The recovery
   * system acks a resync on this, not on the first account's dump — with a
   * batched channel the accounts arrive interleaved, so acking early would
   * report the resync complete while other accounts are still seeding.
   */
  hasDumpedAll: (accountIds: readonly string[]) => boolean
  reset: () => void
}

export function createAccountDumpTracker(): AccountDumpTracker {
  const dumped = new Set<string>()
  return {
    isInitialDump: (accountId) => !dumped.has(accountId),
    markDumped: (accountId) => {
      dumped.add(accountId)
    },
    hasDumped: (accountId) => dumped.has(accountId),
    hasDumpedAll: (accountIds) => accountIds.every((accountId) => dumped.has(accountId)),
    reset: () => {
      dumped.clear()
    },
  }
}
